// frontend/src/pages/agency/AgencyPage.js
import React, { useState, useEffect } from 'react';
import { agencyAPI } from '../../services/api';
import { FiUpload, FiMail, FiPhone, FiGlobe, FiMapPin, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000'}${url}`;
  }
  return url;
};

const AgencyPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agency, setAgency] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: 'Mali',
    website: '',
    description: '',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchAgencyDetails = async () => {
    try {
      setLoading(true);
      const { data } = await agencyAPI.getAgency();
      setAgency(data);
      setForm({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || 'Mali',
        website: data.website || '',
        description: data.description || '',
      });
      if (data.logo_url) {
        setLogoPreview(getFileUrl(data.logo_url));
      }
    } catch (error) {
      console.error('Error fetching agency:', error);
      toast.error("Impossible de charger les détails de l'agence");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencyDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('address', form.address);
      formData.append('city', form.city);
      formData.append('country', form.country);
      formData.append('website', form.website);
      formData.append('description', form.description);

      if (logoFile) {
        formData.append('file', logoFile);
      }

      await agencyAPI.updateAgency(formData);
      toast.success("Détails de l'agence mis à jour !");
      await fetchAgencyDetails();
    } catch (error) {
      console.error('Error updating agency:', error);
      toast.error(error.response?.data?.error || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 12 }}>
        <FiLoader className="spin" size={32} style={{ color: 'var(--primary)' }} />
        <span style={{ color: '#64748b', fontStyle: 'italic' }}>Chargement de l'agence...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Paramètres de l'Agence</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid-2">
          {/* Colonne gauche - Logo et Description */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header">
                <h2>Identité Visuelle</h2>
              </div>
              <div className="card-body" style={{ textAlign: 'center', padding: '24px' }}>
                <div style={{
                  width: 140, height: 140, borderRadius: 12, border: '2px dashed #cbd5e1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                  overflow: 'hidden', background: '#f8fafc', position: 'relative'
                }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo Agence" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: 40, opacity: 0.3 }}>🏢</span>
                  )}
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label htmlFor="logo-upload" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: '0 auto' }}>
                    <FiUpload /> Choisir un logo
                  </label>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                  />
                  <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                    Format recommandé : PNG, JPG (carré, max 10 Mo)
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2>Description & Présentation</h2>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>À propos de l'agence</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={6}
                    value={form.description}
                    onChange={handleInputChange}
                    placeholder="Présentez votre agence immobilière, vos spécialités, vos valeurs..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite - Formulaire */}
          <div className="card">
            <div className="card-header">
              <h2>Informations Générales</h2>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label>Nom de l'agence *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={form.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Téléphone professionnel</label>
                  <div style={{ position: 'relative' }}>
                    <FiPhone style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="text"
                      name="phone"
                      className="form-control"
                      style={{ paddingLeft: 36 }}
                      value={form.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email de contact</label>
                  <div style={{ position: 'relative' }}>
                    <FiMail style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      style={{ paddingLeft: 36 }}
                      value={form.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Adresse physique</label>
                <div style={{ position: 'relative' }}>
                  <FiMapPin style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    name="address"
                    className="form-control"
                    style={{ paddingLeft: 36 }}
                    value={form.address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Ville</label>
                  <input
                    type="text"
                    name="city"
                    className="form-control"
                    value={form.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Pays</label>
                  <input
                    type="text"
                    name="country"
                    className="form-control"
                    value={form.country}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Site internet</label>
                <div style={{ position: 'relative' }}>
                  <FiGlobe style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="url"
                    name="website"
                    className="form-control"
                    style={{ paddingLeft: 36 }}
                    value={form.website}
                    onChange={handleInputChange}
                    placeholder="https://www.monagence.com"
                  />
                </div>
              </div>

              {agency?.subscription_status && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                  <FiCheckCircle style={{ color: '#16a34a', flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: '#15803d' }}>
                    Abonnement actif : <strong>{agency.plan_name || 'Starter'}</strong> · Max {agency.max_properties} biens · Max {agency.max_users} utilisateurs.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="submit" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} disabled={saving}>
                  {saving ? <FiLoader className="spin" /> : <FiCheckCircle />}
                  Enregistrer les modifications
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AgencyPage;
